import { jsPDF } from "jspdf";
import autoTable, { CellHookData, RowInput, UserOptions } from "jspdf-autotable";

import { EstadoProyectoCc } from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { fechaHora, huellaCorta } from "../cc-formato";

/** Un color RGB para jsPDF. */
export type ColorPdf = [number, number, number];

/** Tinta negra del formato, el gris de Norman (71,85,105) y el rojo de la marca. */
const TINTA: ColorPdf = [17, 24, 39];
const GRIS: ColorPdf = [71, 85, 105];
const ROJO: ColorPdf = [183, 28, 28];
const VERDE: ColorPdf = [21, 128, 61];
const RAYA: ColorPdf = [209, 213, 219];
const VERDE_FONDO: ColorPdf = [240, 253, 244];
const ROJO_FONDO: ColorPdf = [254, 242, 242];
const NEUTRO_FONDO: ColorPdf = [241, 245, 249];
/** Márgenes de la hoja carta, en milímetros. */
const MARGEN = 15;
const MARGEN_INF = 22;
const FUENTE = "helvetica";
/** El rótulo de una ficha: 7 pt; si no cabe en su columna se parte en renglones de 1.2 veces su alto (unos 3 mm). */
const ROTULO_PT = 7;
const ROTULO_INTERLINEA = 1.2;
export const RENGLON_ROTULO = (ROTULO_PT * ROTULO_INTERLINEA * 25.4) / 72;

/** Un rótulo con su valor, para las fichas. */
export interface DatoPdf {
  rotulo: string;
  valor: string;
}

/** Lo que va al pie de cada página. */
export interface PiePdf {
  fuente: string;
  folio: string;
  huella: string;
}

/** Una columna de tabla: encabezado, alineación y si se pinta por resultado. */
export interface ColumnaPdf {
  titulo: string;
  ancho?: number;
  derecha?: boolean;
  /** La celda se pinta verde/rojo/gris según su texto (Pasa, Falla, Sin dato, Ignorada). */
  resultado?: boolean;
}

const COLOR_RESULTADO: Record<string, ColorPdf> = {
  Pasa: VERDE,
  "No pasa": ROJO,
  Falla: ROJO,
};

/**
 * Helvetica solo trae WinAnsi: los signos ≥ ≤ → y el espacio fino que usa
 * la pantalla no existen ahí y saldrían como basura.
 */
export function textoPdf(texto: string | null | undefined): string {
  return (texto ?? "")
    .replaceAll(" ", " ")
    .replaceAll("≥", ">=")
    .replaceAll("≤", "<=")
    .replaceAll("→", "->")
    .replaceAll("✓", "Pasa")
    .replaceAll("✕", "Falla");
}

/** El fondo, el borde y el rótulo de la caja del veredicto por estado. */
const VEREDICTO: Record<EstadoProyectoCc, { fondo: ColorPdf; borde: ColorPdf; rotulo: string }> = {
  PASA: { fondo: VERDE_FONDO, borde: VERDE, rotulo: "PASA" },
  NO_PASA: { fondo: ROJO_FONDO, borde: ROJO, rotulo: "NO PASA" },
  SIN_ANALISIS: { fondo: NEUTRO_FONDO, borde: GRIS, rotulo: "SIN ANÁLISIS" },
};

/**
 * El reporte imprimible del módulo: documento real por secciones (texto, no
 * captura de pantalla), con el mismo armado que PdfExpediente de la matriz
 * de riesgos. Carta, márgenes de 15 mm, tinta negra y gris (71,85,105),
 * tablas con jspdf-autotable y el pie con fuente, folio, huella, «Página n
 * de m» y «Uso interno» en cada página.
 */
export class PdfCc {
  readonly pdf = new jsPDF("p", "mm", "letter");
  readonly pageW: number = this.pdf.internal.pageSize.getWidth();
  readonly pageH: number = this.pdf.internal.pageSize.getHeight();
  readonly contentW = this.pageW - 2 * MARGEN;
  /** Dónde va el siguiente renglón. */
  y = MARGEN;

  tinta(): void {
    this.pdf.setTextColor(TINTA[0], TINTA[1], TINTA[2]);
  }

  gris(): void {
    this.pdf.setTextColor(GRIS[0], GRIS[1], GRIS[2]);
  }

  /** Salta de página si lo que sigue ya no cabe. */
  asegura(alto: number): void {
    if (this.y + alto > this.pageH - MARGEN_INF) {
      this.pdf.addPage();
      this.y = MARGEN;
    }
  }

  /** La marca, el título del reporte y el folio a la derecha, con la raya roja. */
  encabezado(titulo: string, subtitulo: string, derecha: string[]): void {
    this.pdf.setFont(FUENTE, "bold");
    this.pdf.setFontSize(8.5);
    this.pdf.setTextColor(ROJO[0], ROJO[1], ROJO[2]);
    this.pdf.text("CENTRO OPERATIVO SOLSER · CALIDAD DE CÓDIGO", MARGEN, this.y + 3);
    this.tinta();
    this.pdf.setFontSize(17);
    const lineasTitulo: string[] = this.pdf.splitTextToSize(textoPdf(titulo), this.contentW - 62);
    // El título (17 pt) baja 7 mm por renglón: el subtítulo va debajo del último, nunca encima.
    this.pdf.text(lineasTitulo, MARGEN, this.y + 11);
    const bajoTitulo = this.y + 11 + (lineasTitulo.length - 1) * 7;
    this.pdf.setFont(FUENTE, "normal");
    this.pdf.setFontSize(9.5);
    this.gris();
    this.pdf.text(textoPdf(subtitulo), MARGEN, bajoTitulo + 5.5);
    this.pdf.setFontSize(8.5);
    let yy = this.y + 3;
    for (const linea of derecha) {
      this.pdf.text(textoPdf(linea), this.pageW - MARGEN, yy, { align: "right" });
      yy += 4.2;
    }
    const abajo = Math.max(bajoTitulo + 8.5, yy + 2);
    this.pdf.setDrawColor(ROJO[0], ROJO[1], ROJO[2]);
    this.pdf.setLineWidth(0.8);
    this.pdf.line(MARGEN, abajo, this.pageW - MARGEN, abajo);
    this.y = abajo + 6;
  }

  /** La caja del veredicto: PASA / NO PASA / SIN ANÁLISIS con su explicación. */
  veredicto(estado: EstadoProyectoCc, texto: string): void {
    const { fondo, borde, rotulo } = VEREDICTO[estado] ?? VEREDICTO.SIN_ANALISIS;
    this.pdf.setFont(FUENTE, "normal");
    this.pdf.setFontSize(9.5);
    const lineas: string[] = this.pdf.splitTextToSize(textoPdf(texto), this.contentW - 52);
    const alto = Math.max(14, lineas.length * 4.6 + 6);
    this.asegura(alto + 4);
    this.pdf.setFillColor(fondo[0], fondo[1], fondo[2]);
    this.pdf.setDrawColor(borde[0], borde[1], borde[2]);
    this.pdf.setLineWidth(0.4);
    this.pdf.roundedRect(MARGEN, this.y, this.contentW, alto, 2, 2, "FD");
    this.pdf.setFont(FUENTE, "bold");
    this.pdf.setFontSize(15);
    this.pdf.setTextColor(borde[0], borde[1], borde[2]);
    this.pdf.text(rotulo, MARGEN + 5, this.y + alto / 2 + 2);
    this.pdf.setFont(FUENTE, "normal");
    this.pdf.setFontSize(9.5);
    this.tinta();
    this.pdf.text(lineas, MARGEN + 50, this.y + 6);
    this.y += alto + 5;
  }

  /** Título de sección con su raya. */
  titulo(txt: string): void {
    this.asegura(14);
    this.pdf.setFont(FUENTE, "bold");
    this.pdf.setFontSize(11);
    this.tinta();
    this.pdf.text(textoPdf(txt), MARGEN, this.y + 4);
    this.pdf.setDrawColor(TINTA[0], TINTA[1], TINTA[2]);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(MARGEN, this.y + 6, this.pageW - MARGEN, this.y + 6);
    this.y += 10;
  }

  parrafo(txt: string): void {
    this.pdf.setFont(FUENTE, "normal");
    this.pdf.setFontSize(9.5);
    this.tinta();
    const lineas: string[] = this.pdf.splitTextToSize(textoPdf(txt?.trim() ? txt : "—"), this.contentW);
    for (const ln of lineas) {
      this.asegura(5);
      this.pdf.text(ln, MARGEN, this.y + 3.5);
      this.y += 4.8;
    }
    this.y += 2;
  }

  /**
   * Rótulos en gris mayúsculas con su valor debajo, en columnas. El rótulo, como el valor, se parte en renglones si no
   * cabe en su columna (antes uno largo se salía de la hoja: «… MANTENIBILI»); los valores de una fila empiezan a la
   * misma altura, bajo el rótulo que más renglones ocupa.
   */
  ficha(datos: DatoPdf[], columnas = 3): void {
    const anchoCol = this.contentW / columnas;
    for (let i = 0; i < datos.length; i += columnas) {
      const fila = datos.slice(i, i + columnas);
      this.pdf.setFont(FUENTE, "bold");
      this.pdf.setFontSize(ROTULO_PT);
      const lineasRotulo = fila.map((d) => this.pdf.splitTextToSize(textoPdf(d.rotulo).toUpperCase(), anchoCol - 4) as string[]);
      this.pdf.setFont(FUENTE, "normal");
      this.pdf.setFontSize(9);
      const lineasValor = fila.map((d) => this.pdf.splitTextToSize(textoPdf(d.valor || "—"), anchoCol - 4) as string[]);
      const bajoRotulo = (Math.max(...lineasRotulo.map((l) => l.length)) - 1) * RENGLON_ROTULO;
      const alto = 4 + bajoRotulo + Math.max(...lineasValor.map((l) => l.length)) * 4.2 + 3;
      this.asegura(alto);
      fila.forEach((d, j) => {
        const x = MARGEN + j * anchoCol;
        this.pdf.setFont(FUENTE, "bold");
        this.pdf.setFontSize(ROTULO_PT);
        this.gris();
        this.pdf.text(lineasRotulo[j], x, this.y + 3, { lineHeightFactor: ROTULO_INTERLINEA });
        this.pdf.setFont(FUENTE, "normal");
        this.pdf.setFontSize(9);
        this.tinta();
        this.pdf.text(lineasValor[j], x, this.y + 7.5 + bajoRotulo);
      });
      this.y += alto;
    }
    this.y += 2;
  }

  /** Una tabla con jspdf-autotable: cabecera con raya negra, cuerpo en tinta, resultados en color. */
  tabla(columnas: ColumnaPdf[], filas: string[][]): void {
    this.asegura(18);
    const estilosColumna: UserOptions["columnStyles"] = {};
    columnas.forEach((c, i) => {
      estilosColumna[i] = { halign: c.derecha ? "right" : "left", cellWidth: c.ancho ?? "auto" };
    });
    const cuerpo: RowInput[] = filas.map((f) => f.map((celda) => textoPdf(celda)));
    autoTable(this.pdf, {
      startY: this.y,
      margin: { left: MARGEN, right: MARGEN, bottom: MARGEN_INF },
      head: [columnas.map((c) => textoPdf(c.titulo))],
      body: cuerpo.length > 0 ? cuerpo : [[{ content: "Sin datos", colSpan: columnas.length }]],
      theme: "plain",
      styles: { font: FUENTE, fontSize: 8.5, cellPadding: 1.6, textColor: TINTA, lineColor: RAYA, lineWidth: { bottom: 0.2 }, overflow: "linebreak" },
      headStyles: { fontStyle: "bold", fontSize: 8, textColor: TINTA, lineColor: TINTA, lineWidth: { bottom: 0.4 } },
      columnStyles: estilosColumna,
      didParseCell: (d: CellHookData) => this.pintaResultado(columnas, d),
    });
    const final = (this.pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY;
    this.y = (final ?? this.y) + 6;
  }

  private pintaResultado(columnas: ColumnaPdf[], d: CellHookData): void {
    if (d.section !== "body" || !columnas[d.column.index]?.resultado) {
      return;
    }
    const texto = Array.isArray(d.cell.text) ? d.cell.text.join(" ") : String(d.cell.text);
    const color = COLOR_RESULTADO[texto.trim()] ?? GRIS;
    d.cell.styles.textColor = color;
    d.cell.styles.fontStyle = "bold";
  }

  /**
   * El pie de cada página y la descarga con el nombre del folio. Dos renglones
   * de fuente a lo ancho y, debajo, «Uso interno» a la izquierda y folio,
   * huella y «Página n de m» a la derecha: nada se encima.
   */
  terminar(pie: PiePdf, archivo: string): void {
    const paginas = this.pdf.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      this.pdf.setPage(p);
      this.pdf.setDrawColor(RAYA[0], RAYA[1], RAYA[2]);
      this.pdf.setLineWidth(0.2);
      this.pdf.line(MARGEN, this.pageH - 16, this.pageW - MARGEN, this.pageH - 16);
      this.pdf.setFont(FUENTE, "normal");
      this.pdf.setFontSize(7.2);
      this.gris();
      const fuente: string[] = this.pdf.splitTextToSize(textoPdf(pie.fuente), this.contentW);
      this.pdf.text(fuente.slice(0, 2), MARGEN, this.pageH - 12.8, { lineHeightFactor: 1.25 });
      this.pdf.text(`Folio ${pie.folio} · huella ${huellaCorta(pie.huella)} · Página ${p} de ${paginas}`, this.pageW - MARGEN, this.pageH - 5.5, { align: "right" });
      this.pdf.setFont(FUENTE, "bold");
      this.pdf.text("Uso interno: contiene proyectos de clientes.", MARGEN, this.pageH - 5.5);
    }
    this.pdf.save(archivo);
  }
}

/** "Emitido el 07/10/2026 10:31 por Norman Estrada". */
export function emitidoTexto(fecha: string | null | undefined, por: string | null | undefined): string {
  return `Emitido el ${fechaHora(fecha)} por ${por || "—"}`;
}
